import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { Router } from '@angular/router';
import { ReponseBase } from './reponse-base';
import { VenteEntite } from '../entites/vente';
import { BaseService, CacheMap } from './base.service';
import { MessageService, LazyLoadEvent } from 'primeng/api';
import { ReponseFiltre } from './reponses/reponse-filter';

/**
 * @service VenteService
 * @description Service for managing Vente (Sales) operations.
 * Provides CRUD operations and data management for sales records.
 */
@Injectable({ providedIn: 'root' })
export class VenteService extends BaseService {

    private ventesUrl = environment.apiBaseUrl + 'vente';
    private venteDetailUrl = environment.apiBaseUrl + 'vente/{venteId}';
    private creerVenteUrl = environment.apiBaseUrl + 'vente';
    private modifierVenteUrl = environment.apiBaseUrl + 'vente';
    private supprimerVenteUrl = environment.apiBaseUrl + 'vente/{venteId}';
    private clientsUrl = environment.apiBaseUrl + 'vente/clients/existants';

    private cacheMapVentes = new CacheMap<string, Observable<ReponseFiltre<VenteEntite>>>();

    public httpOptions = {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
    };

    constructor(
        private http: HttpClient,
        public router: Router,
        public messageService: MessageService
    ) {
        super(messageService, router);
    }

    /**
     * Retrieves a paginated list of ventes with filtering and sorting
     * @param event LazyLoadEvent containing pagination, filtering, and sorting parameters
     * @returns Observable<ReponseFiltre<VenteEntite>> Filtered and paginated ventes data
     */
    public ObtenirVentes(event: LazyLoadEvent): Observable<ReponseFiltre<VenteEntite>> {
        const cacheKey = JSON.stringify(event);
        
        if (!this.cacheMapVentes.has(cacheKey)) {
            const params = this.GenerateFilterParams(event);
            const request = this.http.get<ReponseBase<ReponseFiltre<VenteEntite>>>(this.ventesUrl, { params })
                .pipe(
                    catchError(error => {
                        this.handleError(error);
                        this.cacheMapVentes.delete(cacheKey);
                        throw error;
                    }),
                    shareReplay()
                );
            
            this.cacheMapVentes.set(cacheKey, request.pipe(map(response => response.valeur)));
        }

        return this.cacheMapVentes.get(cacheKey)!;
    }

    /**
     * Retrieves a specific vente by ID
     * @param venteId The ID of the vente to retrieve
     * @returns Promise<ReponseBase<VenteEntite>> The vente data
     */
    public ObtenirVente(venteId: number): Promise<ReponseBase<VenteEntite>> {
        const url = this.venteDetailUrl.replace('{venteId}', venteId.toString());
        const promise = this.http.get<ReponseBase<VenteEntite>>(url).toPromise();

        promise.catch(() => this.router.navigateByUrl('error'));

        return promise;
    }

    /**
     * Retrieves the list of existing clients
     * @returns Observable<ReponseBase<string[]>> List of client names
     */
    public ObtenirClients(): Observable<ReponseBase<string[]>> {
        return this.http.get<ReponseBase<string[]>>(this.clientsUrl);
    }

    /**
     * Creates a new vente
     * @param requete The vente data to create
     * @returns Promise<ReponseBase<number>> The created vente ID
     */
    public CreerVente(requete: VenteEntite): Promise<ReponseBase<number>> {
        const promise = this.http.post<ReponseBase<number>>(this.creerVenteUrl, JSON.stringify(requete), this.httpOptions).toPromise();

        promise.catch(() => this.router.navigateByUrl('error'));

        return promise;
    }

    /**
     * Updates an existing vente
     * @param requete The vente data to update
     * @returns Promise<ReponseBase<VenteEntite>> The updated vente data
     */
    public ModifierVente(requete: VenteEntite): Promise<ReponseBase<VenteEntite>> {
        const promise = this.http.put<ReponseBase<VenteEntite>>(this.modifierVenteUrl, JSON.stringify(requete), this.httpOptions).toPromise();

        promise.catch(() => this.router.navigateByUrl('error'));

        return promise;
    }

    /**
     * Deletes a vente by ID
     * @param venteId The ID of the vente to delete
     * @returns Promise<ReponseBase<boolean>> Success status of the deletion
     */
    public SupprimerVente(venteId: number): Promise<ReponseBase<boolean>> {
        const url = this.supprimerVenteUrl.replace('{venteId}', venteId.toString());
        const promise = this.http.delete<ReponseBase<boolean>>(url, this.httpOptions).toPromise();

        promise.catch(() => this.router.navigateByUrl('error'));

        return promise;
    }

    /**
     * Clears the ventes cache
     */
    public ClearCacheVentes(): void {
        this.cacheMapVentes.clear();
    }

    /**
     * Removes cached pages that contain a specific vente ID
     * @param venteId The vente ID to remove from cache
     */
    public RemovePagesWithVenteId(venteId: number): void {
        const pagesToRemove: string[] = [];
        this.cacheMapVentes.forEach((value, key) => {
            value.subscribe(response => {
                if (response.elements.some(vente => vente.venteId === venteId)) {
                    pagesToRemove.push(key);
                }
            });
        });
        pagesToRemove.forEach(key => this.cacheMapVentes.delete(key));
    }
}
